'use client';
import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { notify } from '@/lib/toast';
import { MessageSquare, X, Send, ArrowLeft, Loader2, Zap, Settings } from 'lucide-react';
import { useAuthStore } from '@/store/auth.store';
import { useMessagesStore } from '@/store/messages.store';

// ─── Types ───────────────────────────────────────────────────────────────────

interface ConversationListItem {
  id: string;
  type: 'direct' | 'request' | 'booking';
  contextId: string | null;
  participantAId: string;
  participantBId: string;
  lastMessageAt: string | null;
  createdAt: string;
  otherUser: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    avatarUrl: string | null;
    email: string;
    username?: string | null;
  };
  lastMessage: {
    text: string;
    senderId: string;
    createdAt: string;
  } | null;
  unreadCount: number;
}

interface Message {
  id: string;
  senderId: string;
  text: string;
  isRead: boolean;
  createdAt: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTime(iso: string) {
  const d = new Date(iso);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - d.getTime()) / 86400000);
  if (diffDays === 0) return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  if (diffDays === 1) return 'Вчера';
  if (diffDays < 7) return d.toLocaleDateString('ru-RU', { weekday: 'short' });
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}

function formatMsgTime(iso: string) {
  return new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

function getDisplayName(u: { firstName: string | null; lastName: string | null; email: string }) {
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ');
  return name || u.email;
}

function Avatar({ user, size = 40 }: {
  user: { firstName: string | null; lastName: string | null; avatarUrl: string | null; email: string };
  size?: number;
}) {
  const initials = [user.firstName?.[0], user.lastName?.[0]].filter(Boolean).join('').toUpperCase() || user.email[0].toUpperCase();
  if (user.avatarUrl) {
    return <img src={user.avatarUrl} alt="" style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />;
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      backgroundColor: 'var(--color-brand)', color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.35, fontWeight: 700,
    }}>
      {initials}
    </div>
  );
}

// ─── ConversationList ─────────────────────────────────────────────────────────

function ConversationList({ conversations, selectedId, onSelect, currentUserId }: {
  conversations: ConversationListItem[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  currentUserId: string;
}) {
  if (conversations.length === 0) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--color-text-muted)' }}>
        <MessageSquare size={36} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
        <p style={{ fontSize: '14px', fontWeight: 600 }}>Нет диалогов</p>
        <p style={{ fontSize: '13px', marginTop: 4 }}>Напишите специалисту из его профиля</p>
      </div>
    );
  }
  return (
    <div>
      {conversations.map((conv) => {
        const isActive = conv.id === selectedId;
        const isMine = conv.lastMessage?.senderId === currentUserId;
        return (
          <button key={conv.id} onClick={() => onSelect(conv.id)} style={{
            display: 'flex', alignItems: 'center', gap: '12px',
            width: '100%', padding: '12px 16px', textAlign: 'left',
            background: isActive ? 'var(--color-brand)' : 'transparent',
            border: 'none', borderBottom: '1px solid var(--color-border-subtle)',
            cursor: 'pointer', transition: 'background 0.15s',
          }}>
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <Avatar user={conv.otherUser} size={40} />
              {conv.type !== 'direct' && (
                <span style={{ position: 'absolute', bottom: -2, right: -2, fontSize: '11px', background: 'var(--color-bg-surface)', borderRadius: '50%', width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--color-border)' }}>
                  {conv.type === 'request' ? '📋' : '📅'}
                </span>
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                {conv.otherUser.username ? (
                  <a href={`/u/${conv.otherUser.username}`} onClick={e => e.stopPropagation()} style={{ fontSize: '13px', fontWeight: 600, color: isActive ? '#fff' : 'var(--color-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '130px', textDecoration: 'none' }}>
                    {getDisplayName(conv.otherUser)}
                  </a>
                ) : (
                  <span style={{ fontSize: '13px', fontWeight: 600, color: isActive ? '#fff' : 'var(--color-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '130px' }}>
                    {getDisplayName(conv.otherUser)}
                  </span>
                )}
                {conv.lastMessage && (
                  <span style={{ fontSize: '11px', color: isActive ? 'rgba(255,255,255,0.7)' : 'var(--color-text-muted)', flexShrink: 0, marginLeft: 4 }}>
                    {formatTime(conv.lastMessage.createdAt)}
                  </span>
                )}
              </div>
              {conv.type !== 'direct' && (
                <span style={{ fontSize: '11px', color: isActive ? 'rgba(255,255,255,0.6)' : 'var(--color-text-muted)', display: 'block', marginBottom: 2 }}>
                  {conv.type === 'request' ? `Заказ #${conv.contextId?.slice(-6) ?? ''}` : `Бронь #${conv.contextId?.slice(-6) ?? ''}`}
                </span>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', color: isActive ? 'rgba(255,255,255,0.75)' : 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '150px' }}>
                  {conv.lastMessage ? (isMine ? `Вы: ${conv.lastMessage.text}` : conv.lastMessage.text) : 'Нет сообщений'}
                </span>
                {conv.unreadCount > 0 && !isActive && (
                  <span style={{ backgroundColor: 'var(--color-brand)', color: '#fff', borderRadius: '10px', padding: '1px 6px', fontSize: '11px', fontWeight: 700, flexShrink: 0, marginLeft: 4 }}>
                    {conv.unreadCount}
                  </span>
                )}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ─── ChatWindow ───────────────────────────────────────────────────────────────

function ChatWindow({ conversationId, otherUser, currentUserId, currentUserRole, onBack }: {
  conversationId: string;
  otherUser: ConversationListItem['otherUser'];
  currentUserId: string;
  currentUserRole: string;
  onBack: () => void;
}) {
  const [text, setText] = useState('');
  const [quickOpen, setQuickOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportComment, setReportComment] = useState('');
  const [reportMsgId, setReportMsgId] = useState<string | null>(null);

  // ─── Блокировка ──────────────────────────────────────────────────────────
  const { data: blockData, refetch: refetchBlock } = useQuery<{ blocked: boolean }>({
    queryKey: ['block-status', otherUser.id],
    queryFn: () => api.get('/users/blocks').then(r => ({
      blocked: (r.data.data as any[]).some((b: any) => b.user.id === otherUser.id),
    })),
    staleTime: 30000,
  });
  const isBlocked = blockData?.blocked ?? false;

  const blockMutation = useMutation({
    mutationFn: (block: boolean) => block
      ? api.post('/users/block', { userId: otherUser.id })
      : api.delete(`/users/block/${otherUser.id}`),
    onSuccess: (_, block) => {
      refetchBlock();
      notify.success(block ? 'Пользователь заблокирован' : 'Пользователь разблокирован');
      setSettingsOpen(false);
    },
    onError: () => notify.error('Не удалось выполнить действие'),
  });

  // ─── Жалоба ──────────────────────────────────────────────────────────────
  const reportMutation = useMutation({
    mutationFn: (data: { targetType: string; targetId: string; reason: string; comment?: string }) =>
      api.post('/reports', data),
    onSuccess: () => {
      notify.success('Жалоба принята. Мы рассмотрим её в течение 24 часов');
      setReportOpen(false);
      setReportReason('');
      setReportComment('');
      setSettingsOpen(false);
    },
    onError: (e: any) => {
      const code = e?.response?.data?.error?.code;
      if (code === 'ALREADY_REPORTED') notify.error('Вы уже подавали жалобу на этого пользователя');
      else notify.error('Не удалось отправить жалобу');
    },
  });

  // ─── Настройки уведомлений ───────────────────────────────────────────────
  const { data: notifSettings, refetch: refetchNotifSettings } = useQuery<{
    emailEnabled: boolean; tgEnabled: boolean; maxEnabled: boolean;
  }>({
    queryKey: ['msg-notif-settings'],
    queryFn: () => api.get('/messages/notification-settings').then(r => r.data.data),
    staleTime: 60000,
  });
  const notifMutation = useMutation({
    mutationFn: (patch: { emailEnabled?: boolean; tgEnabled?: boolean; maxEnabled?: boolean }) =>
      api.patch('/messages/notification-settings', patch).then(r => r.data.data),
    onSuccess: () => refetchNotifSettings(),
    onError: () => notify.error('Не удалось сохранить настройки'),
  });
  // Данные специалиста — запрос всегда, 404 → null
  const { data: spData } = useQuery<any>({
    queryKey: ['specialist-profile-me'],
    queryFn: () => api.get('/specialists/me/profile').then(r => r.data.data).catch(() => null),
    staleTime: 60000,
  });
  const isSpecialist = !!spData;
  const { data: meData } = useQuery<any>({
    queryKey: ['me'],
    queryFn: () => api.get('/users/me').then(r => r.data.data),
    staleTime: 60000,
    enabled: isSpecialist,
  });

  const handleQuickAction = (type: 'contacts' | 'address' | 'schedule' | 'services') => {
    if (type === 'contacts') {
      if (!meData?.phone) { notify.error('Заполните телефон в профиле'); return; }
      const parts = [`📞 ${meData.phone}`];
      if (meData.email) parts.push(`✉️ ${meData.email}`);
      setText(parts.join('\n'));
    } else if (type === 'address') {
      const addr = meData?.city || spData?.city;
      if (!addr) { notify.error('Укажите город/адрес в профиле'); return; }
      setText(`📍 ${addr}`);
    } else if (type === 'schedule') {
      const username = spData?.username;
      if (!username) { notify.error('Заполните профиль специалиста'); return; }
      setText(`📅 Моё расписание и запись: https://svoyprofi.ru/u/${username}`);
    } else if (type === 'services') {
      const username = spData?.username;
      if (!username) { notify.error('Заполните профиль специалиста'); return; }
      setText(`💼 Мои услуги: https://svoyprofi.ru/u/${username}#services`);
    }
    setQuickOpen(false);
  };
  const bottomRef = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();

  const { data: messages = [], isLoading } = useQuery<Message[]>({
    queryKey: ['messages', conversationId],
    queryFn: () => api.get(`/messages/${conversationId}`).then(r => r.data.data?.messages ?? r.data.data ?? []),
    refetchInterval: 5000,
    staleTime: 0,
  });

  useEffect(() => {
    if (messages.length > 0) {
      api.patch(`/messages/${conversationId}/read`).then(() => {
        qc.invalidateQueries({ queryKey: ['conversations'] });
        qc.invalidateQueries({ queryKey: ['messages-unread-count'] });
      }).catch(() => {});
    }
  }, [conversationId, messages.length]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMutation = useMutation({
    mutationFn: (body: string) => api.post(`/messages/${conversationId}`, { body }),
    onSuccess: () => {
      setText('');
      qc.invalidateQueries({ queryKey: ['messages', conversationId] });
      qc.invalidateQueries({ queryKey: ['conversations'] });
      qc.invalidateQueries({ queryKey: ['messages-unread-count'] });
    },
    onError: (err: any) => notify.apiError(err, 'Ошибка отправки'),
  });

  const handleSend = () => {
    const trimmed = text.trim();
    if (!trimmed || sendMutation.isPending) return;
    sendMutation.mutate(trimmed);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const grouped: { date: string; msgs: Message[] }[] = [];
  for (const msg of messages) {
    const date = new Date(msg.createdAt).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
    const last = grouped[grouped.length - 1];
    if (last && last.date === date) last.msgs.push(msg);
    else grouped.push({ date, msgs: [msg] });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderBottom: '1px solid var(--color-border-subtle)', flexShrink: 0 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center' }}>
          <ArrowLeft size={18} />
        </button>
        <Avatar user={otherUser} size={32} />
        {otherUser.username ? (
          <a href={`/u/${otherUser.username}`} style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-text-primary)', margin: 0, textDecoration: 'none' }}>
            {getDisplayName(otherUser)}
          </a>
        ) : (
          <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-text-primary)', margin: 0 }}>{getDisplayName(otherUser)}</p>
        )}
        {/* Кнопка настроек — прижата вправо */}
        <div style={{ marginLeft: 'auto', position: 'relative' }}>
          <button
            onClick={() => setSettingsOpen(v => !v)}
            title="Настройки уведомлений"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: settingsOpen ? 'var(--color-brand)' : 'var(--color-text-muted)', display: 'flex', alignItems: 'center' }}
          >
            <Settings size={16} />
          </button>
          {settingsOpen && (
            <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 100, backgroundColor: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: '10px', padding: '12px 14px', minWidth: '210px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}>
              <p style={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-text-muted)', margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Уведомления о сообщениях</p>
              {([
                { key: 'emailEnabled', label: '✉️ Email' },
                { key: 'tgEnabled',    label: '✈️ Telegram' },
                { key: 'maxEnabled',   label: '🤖 MAX бот' },
              ] as const).map(({ key, label }) => {
                const enabled = notifSettings ? notifSettings[key] : true;
                return (
                  <label key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', cursor: 'pointer' }}>
                    <span style={{ fontSize: '13px', color: 'var(--color-text-primary)' }}>{label}</span>
                    <div
                      onClick={() => notifMutation.mutate({ [key]: !enabled })}
                      style={{ width: 36, height: 20, borderRadius: 10, backgroundColor: enabled ? 'var(--color-brand)' : 'var(--color-border)', position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0 }}
                    >
                      <div style={{ position: 'absolute', top: 2, left: enabled ? 18 : 2, width: 16, height: 16, borderRadius: '50%', backgroundColor: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                    </div>
                  </label>
                );
              })}
              {/* Разделитель */}
              <div style={{ borderTop: '1px solid var(--color-border-subtle)', margin: '10px 0' }} />
              <p style={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-text-muted)', margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Безопасность</p>
              <button
                onClick={() => blockMutation.mutate(!isBlocked)}
                disabled={blockMutation.isPending}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0', fontSize: '13px', color: isBlocked ? 'var(--color-brand)' : 'var(--color-danger)', marginBottom: '6px' }}
              >
                {isBlocked ? '✅ Разблокировать' : '🚫 Заблокировать'}
              </button>
              <button
                onClick={() => { setReportOpen(true); }}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0', fontSize: '13px', color: 'var(--color-text-secondary)' }}
              >
                ⚠️ Пожаловаться
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Модалка жалобы */}
      {reportOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 200, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ backgroundColor: 'var(--color-bg-surface)', borderRadius: '14px', padding: '20px', width: '100%', maxWidth: '360px', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 4px', fontSize: '15px', fontWeight: 700, color: 'var(--color-text-primary)' }}>Пожаловаться</h3>
            <p style={{ margin: '0 0 14px', fontSize: '12px', color: 'var(--color-text-muted)' }}>На пользователя {otherUser.firstName || otherUser.lastName || 'пользователя'}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
              {([
                { value: 'spam',          label: '💬 Спам / реклама' },
                { value: 'insult',        label: '🤬 Оскорбления / грубость' },
                { value: 'data_phishing', label: '🎣 Выманивание личных данных' },
                { value: 'no_show',       label: '👻 Не явился / игнорирует' },
                { value: 'illegal',       label: '🚨 Незаконная деятельность' },
                { value: 'other',         label: '🚫 Другое' },
              ] as const).map(({ value, label }) => (
                <label key={value} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: 'var(--color-text-primary)' }}>
                  <input
                    type="radio"
                    name="report-reason"
                    value={value}
                    checked={reportReason === value}
                    onChange={() => setReportReason(value)}
                    style={{ accentColor: 'var(--color-brand)' }}
                  />
                  {label}
                </label>
              ))}
            </div>
            <textarea
              value={reportComment}
              onChange={e => setReportComment(e.target.value)}
              placeholder="Комментарий (необязательно)"
              rows={2}
              maxLength={500}
              style={{ width: '100%', resize: 'none', border: '1px solid var(--color-border-input)', borderRadius: '8px', padding: '8px', fontSize: '13px', fontFamily: 'inherit', backgroundColor: 'var(--color-bg-subtle)', color: 'var(--color-text-primary)', boxSizing: 'border-box', marginBottom: '14px' }}
            />
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => { setReportOpen(false); setReportReason(''); setReportComment(''); setReportMsgId(null); }}
                style={{ flex: 1, padding: '9px', borderRadius: '8px', border: '1px solid var(--color-border)', background: 'none', cursor: 'pointer', fontSize: '13px', color: 'var(--color-text-secondary)' }}
              >
                Отмена
              </button>
              <button
                onClick={() => { reportMutation.mutate({ targetType: reportMsgId ? 'message' : 'user', targetId: reportMsgId ?? otherUser.id, reason: reportReason, comment: reportComment || undefined }); }}
                disabled={!reportReason || reportMutation.isPending}
                style={{ flex: 1, padding: '9px', borderRadius: '8px', border: 'none', backgroundColor: reportReason ? 'var(--color-danger)' : 'var(--color-border)', color: '#fff', cursor: reportReason ? 'pointer' : 'not-allowed', fontSize: '13px', fontWeight: 600 }}
              >
                {reportMutation.isPending ? 'Отправка...' : 'Отправить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {isLoading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-brand)' }} />
          </div>
        )}
        {!isLoading && messages.length === 0 && (
          <div style={{ textAlign: 'center', color: 'var(--color-text-muted)', padding: '40px 20px' }}>
            <p style={{ fontSize: '13px' }}>Начните диалог — отправьте первое сообщение</p>
          </div>
        )}
        {grouped.map(({ date, msgs }) => (
          <div key={date}>
            <div style={{ textAlign: 'center', margin: '10px 0 6px' }}>
              <span style={{ fontSize: '11px', color: 'var(--color-text-muted)', backgroundColor: 'var(--color-border-subtle)', borderRadius: '12px', padding: '2px 10px' }}>{date}</span>
            </div>
            {msgs.map((msg) => {
              const isMine = msg.senderId === currentUserId;
              return (
                <div key={msg.id} style={{ display: 'flex', justifyContent: isMine ? 'flex-end' : 'flex-start', marginBottom: '5px', alignItems: 'flex-end', gap: '4px' }}>
                  {!isMine && (
                    <button onClick={() => { setReportMsgId(msg.id); setReportOpen(true); }} title="Пожаловаться на сообщение" style={{ flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: 'var(--color-text-muted)', opacity: 0.15, transition: 'opacity 0.2s, color 0.2s' }}
                      onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.color = '#f59e0b'; }} onMouseLeave={e => { e.currentTarget.style.opacity = '0.15'; e.currentTarget.style.color = 'var(--color-text-muted)'; }}>
                      ⚑
                    </button>
                  )}
                  <div style={{ maxWidth: '75%', padding: '7px 11px', borderRadius: isMine ? '14px 14px 4px 14px' : '14px 14px 14px 4px', backgroundColor: isMine ? 'var(--color-brand)' : 'var(--color-bg-subtle)', border: isMine ? 'none' : '1px solid var(--color-border-subtle)' }}>
                    <p style={{ fontSize: '13px', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: isMine ? '#fff' : 'var(--color-text-primary)', margin: 0 }}>{msg.text}</p>
                    <p style={{ fontSize: '10px', marginTop: '3px', textAlign: 'right', color: isMine ? 'rgba(255,255,255,0.65)' : 'var(--color-text-muted)' }}>{formatMsgTime(msg.createdAt)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      {/* Quick Actions Panel */}
      {isSpecialist && quickOpen && (
        <div style={{ padding: '8px 12px', borderTop: '1px solid var(--color-border-subtle)', display: 'flex', gap: '6px', flexWrap: 'wrap', flexShrink: 0, backgroundColor: 'var(--color-bg-subtle)' }}>
          {([
            { type: 'contacts', label: '📞 Контакты' },
            { type: 'address',  label: '📍 Адрес' },
            { type: 'schedule', label: '📅 Расписание' },
            { type: 'services', label: '💼 Услуги' },
          ] as const).map(({ type, label }) => (
            <button key={type} onClick={() => handleQuickAction(type)} style={{ fontSize: '12px', padding: '4px 10px', borderRadius: '16px', border: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-surface)', color: 'var(--color-text-ui)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {label}
            </button>
          ))}
        </div>
      )}
      {/* Input */}
      <div style={{ padding: '10px 12px', borderTop: '1px solid var(--color-border-subtle)', display: 'flex', alignItems: 'flex-end', gap: '8px', flexShrink: 0 }}>
        {isSpecialist && (
          <button onClick={() => setQuickOpen(v => !v)} title="Быстрые действия" style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, backgroundColor: quickOpen ? 'var(--color-brand)' : 'var(--color-bg-subtle)', border: '1px solid var(--color-border)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.15s', alignSelf: 'flex-end', marginBottom: '2px' }}>
            <Zap size={14} color={quickOpen ? '#fff' : 'var(--color-text-secondary)'} />
          </button>
        )}
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Сообщение... (Enter — отправить)"
          rows={1}
          maxLength={4000}
          style={{ flex: 1, resize: 'none', border: '1px solid var(--color-border-input)', borderRadius: '10px', padding: '8px 12px', fontSize: '13px', outline: 'none', lineHeight: 1.5, fontFamily: 'inherit', backgroundColor: 'var(--color-bg-subtle)', color: 'var(--color-text-primary)', maxHeight: '100px', overflowY: 'auto' }}
          onInput={(e) => { const el = e.currentTarget; el.style.height = 'auto'; el.style.height = Math.min(el.scrollHeight, 100) + 'px'; }}
        />
        <button onClick={handleSend} disabled={!text.trim() || sendMutation.isPending} style={{ width: 36, height: 36, borderRadius: '50%', flexShrink: 0, backgroundColor: text.trim() ? 'var(--color-brand)' : 'var(--color-border)', border: 'none', cursor: text.trim() ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.15s' }}>
          {sendMutation.isPending
            ? <Loader2 size={16} color="#fff" style={{ animation: 'spin 1s linear infinite' }} />
            : <Send size={16} color="#fff" />
          }
        </button>
      </div>
    </div>
  );
}

// ─── MessagesButton (exported) ────────────────────────────────────────────────

export default function MessagesButton() {
  const { user, isAuthenticated } = useAuthStore();
  const { openWithUserId, setOpenWithUserId, openWithContext, setOpenWithContext, closeMessages } = useMessagesStore();
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mobileView, setMobileView] = useState<'list' | 'chat'>('list');
  const ref = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();

  // Открыть чат с конкретным пользователем (вызов из SpecialistCard)
  const createConvMutation = useMutation({
    mutationFn: (specialistId: string) =>
      api.post('/messages/conversations', { specialistId }).then(r => r.data.data),
    onSuccess: (conv) => {
      qc.invalidateQueries({ queryKey: ['conversations'] });
      setSelectedId(conv.id);
      setMobileView('chat');
      setOpen(true);
    },
    onError: () => {},
  });

  useEffect(() => {
    if (!openWithUserId) return;
    setOpenWithUserId(null);
    createConvMutation.mutate(openWithUserId);
  }, [openWithUserId]);

  // Открыть чат в контексте заказа/брони
  const createContextConvMutation = useMutation({
    mutationFn: ({ type, contextId, participantId }: { type: string; contextId: string; participantId: string }) =>
      api.post(`/messages/${type}/${contextId}`, { participantId }).then(r => r.data.data),
    onSuccess: (conv) => {
      qc.invalidateQueries({ queryKey: ['conversations'] });
      setSelectedId(conv.id);
      setMobileView('chat');
      setOpen(true);
    },
    onError: () => {},
  });
  useEffect(() => {
    if (!openWithContext) return;
    closeMessages();
    createContextConvMutation.mutate(openWithContext);
  }, [openWithContext]);

  // Список диалогов — polling каждые 15с (только когда открыто или для счётчика)
  const { data: conversations = [] } = useQuery<ConversationListItem[]>({
    queryKey: ['conversations'],
    queryFn: () => api.get('/messages').then(r => r.data.data),
    refetchInterval: open ? 10000 : 30000,
    staleTime: 5000,
    enabled: !!user && isAuthenticated(),
  });

  // Суммарный счётчик непрочитанных
  const unreadCount = conversations.reduce((sum, c) => sum + (c.unreadCount || 0), 0);

  // Авто-выбор первого диалога на десктопе
  useEffect(() => {
    if (open && !selectedId && conversations.length > 0) {
      setSelectedId(conversations[0].id);
    }
  }, [open, conversations, selectedId]);

  // Закрытие по клику вне
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (id: string) => { setSelectedId(id); setMobileView('chat'); };
  const handleClose = () => { setOpen(false); };

  if (!user || !isAuthenticated()) return null;

  const selectedConv = conversations.find(c => c.id === selectedId);

  return (
    <div className="relative" ref={ref}>
      {/* Кнопка в хедере */}
      <button
        onClick={() => setOpen(v => !v)}
        className="relative p-2 text-text-secondary hover:text-brand transition-colors"
        title="Сообщения"
      >
        <MessageSquare size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Модальное окно */}
      {open && (
        <div
          className="bg-bg-surface rounded-2xl shadow-2xl border border-border z-50 overflow-hidden messages-modal-popup"
          style={{
            position: typeof window !== 'undefined' && window.innerWidth < 640
              ? 'fixed' : 'absolute',
            top: typeof window !== 'undefined' && window.innerWidth < 640
              ? '56px' : '44px',
            right: typeof window !== 'undefined' && window.innerWidth < 640
              ? '8px' : '0',
            left: typeof window !== 'undefined' && window.innerWidth < 640
              ? '8px' : 'auto',
            width: typeof window !== 'undefined' && window.innerWidth < 640
              ? 'auto' : '680px',
            height: typeof window !== 'undefined' && window.innerWidth < 640
              ? 'calc(100vh - 72px)' : '520px',
          }}
        >
          {/* Заголовок */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle flex-shrink-0" style={{ height: '52px' }}>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-text-primary text-sm">Сообщения</span>
              {unreadCount > 0 && (
                <span className="text-xs bg-red-500 text-white px-1.5 py-0.5 rounded-full font-bold">{unreadCount}</span>
              )}
            </div>
            <button onClick={handleClose} className="text-gray-400 hover:text-text-secondary transition-colors">
              <X size={16} />
            </button>
          </div>

          {/* Тело: список + переписка */}
          <div style={{ display: 'flex', height: 'calc(520px - 52px)' }}>
            {/* Список диалогов */}
            <div
              style={{
                width: '240px',
                flexShrink: 0,
                borderRight: '1px solid var(--color-border-subtle)',
                overflowY: 'auto',
                display: mobileView === 'chat' ? 'none' : 'block',
              }}
              className="md:block"
            >
              <ConversationList
                conversations={conversations}
                selectedId={selectedId}
                onSelect={handleSelect}
                currentUserId={user.id}
              />
            </div>

            {/* Переписка */}
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: mobileView === 'list' ? 'none' : 'flex',
                flexDirection: 'column',
              }}
              className="md:flex"
            >
              {selectedConv ? (
                <ChatWindow
                  key={selectedConv.id}
                  conversationId={selectedConv.id}
                  otherUser={selectedConv.otherUser}
                  currentUserId={user.id}
                  currentUserRole={user.role}
                  onBack={() => setMobileView('list')}
                />
              ) : (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-muted)', padding: '40px 20px', textAlign: 'center' }}>
                  <MessageSquare size={40} style={{ opacity: 0.2, marginBottom: '12px' }} />
                  <p style={{ fontSize: '14px', fontWeight: 600 }}>Выберите диалог</p>
                  <p style={{ fontSize: '12px', marginTop: 4 }}>Выберите диалог из списка слева</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
